import type { ValidationError } from '@openx/types/api.ts';
import type { DealParticipant } from '@openx/types/deal.ts';
import {
  type DealForecast,
  type DealForecastApiResponse,
  DealForecastStatus,
  type PostForecastDataResponse,
} from '@openx/types/dealForecasting.ts';
import { DealForecastActionType, type DealForecastContextualEstimate } from '@openx/types/store/deal.ts';
import type { Dispatch } from 'react';
import type { Dispatch as ReduxDispatch } from 'redux';

export const defaultFetchError = 'An error occurred while fetching data.';
export const timeoutError = 'The request timeout limit has been exceeded.';

export type DealForecastAction = {
  audience_monthly_requests?: number | null;
  contextual_estimate?: DealForecastContextualEstimate | null;
  deal_participants?: DealParticipant[];
  error?: ValidationError<Error>;
  forecast?: DealForecast;
  not_included_fields?: string[];
  job_id?: string;
  package_loading?: boolean;
  type: string;
  disabled?: boolean;
};

export const cancelForecastAction: DealForecastAction = {
  type: DealForecastActionType.CANCEL_FORECAST,
};

export const dealForecastPackageLoadingAction = (package_loading: boolean): DealForecastAction => ({
  package_loading,
  type: DealForecastActionType.DEAL_FORECAST_PACKAGE_LOADING,
});

export const forecastRequestErrorAction = (error): DealForecastAction => ({
  error,
  type: DealForecastActionType.FORECAST_REQUEST_ERROR,
});

export const forecastRequestLoadingAction = (): DealForecastAction => ({
  type: DealForecastActionType.FORECAST_REQUEST_LOADING,
});

export const getJobIDSuccessAction = (
  response: PostForecastDataResponse,
  audience_monthly_requests: number | null,
  contextual_estimate: DealForecastContextualEstimate | null = null
): DealForecastAction => ({
  audience_monthly_requests,
  contextual_estimate,
  job_id: response.job_id,
  not_included_fields: response.not_included_fields,
  type: DealForecastActionType.GET_JOB_ID_SUCCESS,
});

export const forecastRequestSuccessAction = (forecast?: DealForecast): DealForecastAction => ({
  forecast,
  type: DealForecastActionType.FORECAST_REQUEST_SUCCESS,
});

export const updateDealParticipantsAction = (deal_participants: DealParticipant[]): DealForecastAction => ({
  deal_participants,
  type: DealForecastActionType.FORECAST_UPDATE_DEAL_PARTICIPANTS,
});

export const generateForecastButtonDisabledAction = (disabled: boolean): DealForecastAction => ({
  disabled,
  type: DealForecastActionType.GENERATE_FORECAST_BUTTON_DISABLED,
});

export const resetForecastAction = (): DealForecastAction => ({
  type: DealForecastActionType.RESET_FORECAST,
});

type HandleFetchForecastResponse = {
  data: DealForecastApiResponse;
  reqCount: number;
  updateReqCount: () => void;
  dispatch: Dispatch<DealForecastAction> | ReduxDispatch<DealForecastAction>;
  stopPolling: () => void;
  MAX_REQUEST_COUNT: number;
};

export const handleFetchForecastResponse = ({
  data,
  reqCount,
  updateReqCount,
  dispatch,
  stopPolling,
  MAX_REQUEST_COUNT,
}: HandleFetchForecastResponse) => {
  updateReqCount();
  const { status, output, msg } = data || {};

  if (status) {
    switch (status) {
      case DealForecastStatus.InProgress:
        if (reqCount >= MAX_REQUEST_COUNT) {
          dispatch(forecastRequestErrorAction(timeoutError));
          stopPolling();
        }
        break;
      case DealForecastStatus.Ready:
        dispatch(forecastRequestSuccessAction(output as DealForecast));
        stopPolling();
        break;
      case DealForecastStatus.Error:
        dispatch(forecastRequestErrorAction(msg || defaultFetchError));
        stopPolling();
        break;
    }
  }
};

type HandleFetchForecastError = {
  error;
  dispatch: Dispatch<DealForecastAction> | ReduxDispatch<DealForecastAction>;
  stopPolling: () => void;
};

export const handleFetchForecastError = ({ error, dispatch, stopPolling }: HandleFetchForecastError) => {
  dispatch(forecastRequestErrorAction(error));
  stopPolling();
};
